import React, { useState, useEffect } from "react";
import { Modal, Form, Row } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import moment from "moment";

/**
 * ContractAmendmentModal
 *
 * Props:
 *  isOpen       - boolean
 *  toggle       - fn to close modal
 *  contract     - { id, company_name, value, currency_symbol, startdate, enddate, hours }
 *  onAmendmentSaved - fn(amendment) called after successful save
 */

const CHANGE_REASON_OPTIONS = [
  { label: "Client requested additional scope", value: "additional_scope" },
  { label: "Revised timeline", value: "revised_timeline" },
  { label: "Pricing adjustment", value: "pricing_adjustment" },
  { label: "Resource change", value: "resource_change" },
  { label: "Reduced scope", value: "reduced_scope" },
  { label: "Other", value: "other" },
];

const colourStyles = {
  menuList: (s) => ({ ...s, background: "white" }),
  menu: (s) => ({ ...s, zIndex: 1200 }),
  control: (s) => ({ ...s, minHeight: "36px", fontSize: "13px" }),
  option: (s) => ({ ...s, fontSize: "13px" }),
};

const emptyMilestone = () => ({ name: "", revised_date: "" });
const emptyScope = () => ({ type: "add", description: "" });

const ContractAmendmentModal = ({ isOpen, toggle, contract = {}, onAmendmentSaved }) => {
  const [description, setDescription] = useState("");
  const [changeReason, setChangeReason] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [hoursDirection, setHoursDirection] = useState("add");
  const [hoursChange, setHoursChange] = useState("");
  const [feeDirection, setFeeDirection] = useState("add");
  const [feeChange, setFeeChange] = useState("");
  const [milestones, setMilestones] = useState([emptyMilestone()]);
  const [scopeItems, setScopeItems] = useState([emptyScope()]);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const contractValue = parseFloat(contract?.value) || 0;
  const feeNum = parseFloat(feeChange) || 0;
  const revisedValue = feeDirection === "add" ? contractValue + feeNum : contractValue - feeNum;
  const hoursNum = parseFloat(hoursChange) || 0;
  const currentHours = parseFloat(contract?.hours) || 0;
  const revisedHours = hoursDirection === "add" ? currentHours + hoursNum : currentHours - hoursNum;

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen]);

  const resetForm = () => {
    setDescription("");
    setChangeReason(null);
    setEffectiveDate("");
    setHoursDirection("add");
    setHoursChange("");
    setFeeDirection("add");
    setFeeChange("");
    setMilestones([emptyMilestone()]);
    setScopeItems([emptyScope()]);
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!description.trim()) e.description = "Amendment description is required";
    if (!changeReason) e.changeReason = "Change reason is required";
    if (!effectiveDate) e.effectiveDate = "Effective date is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      contract_id: contract?.id,
      description,
      change_reason: changeReason?.value,
      effective_date: effectiveDate,
      hours_direction: hoursDirection,
      hours_change: hoursNum,
      fee_direction: feeDirection,
      fee_change: feeNum,
      revised_value: revisedValue,
      revised_hours: revisedHours,
      milestones: milestones.filter((m) => m.name || m.revised_date),
      scope_items: scopeItems.filter((s) => s.description),
      created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
    };

    setIsSaving(true);
    try {
      // Replace with your actual API call:
      // const res = await axiosNodeInstance.post("/contract/amendment", payload);
      await new Promise((r) => setTimeout(r, 800));

      Swal.fire({
        icon: "success",
        text: "Amendment created and sent to client for approval.",
        timer: 2500,
        showConfirmButton: false,
      });
      onAmendmentSaved?.(payload);
      toggle();
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", text: "Something went wrong. Please try again." });
    } finally {
      setIsSaving(false);
    }
  };

  // Milestone helpers
  const updateMilestone = (idx, field, val) => {
    const updated = [...milestones];
    updated[idx][field] = val;
    setMilestones(updated);
  };
  const addMilestone = () => setMilestones([...milestones, emptyMilestone()]);
  const removeMilestone = (idx) => setMilestones(milestones.filter((_, i) => i !== idx));

  // Scope helpers
  const updateScope = (idx, field, val) => {
    const updated = [...scopeItems];
    updated[idx][field] = val;
    setScopeItems(updated);
  };
  const addScope = () => setScopeItems([...scopeItems, emptyScope()]);
  const removeScope = (idx) => setScopeItems(scopeItems.filter((_, i) => i !== idx));

  const sym = contract?.currency_symbol || "£";

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
      {/* ── Header ── */}
      <div className="modal-header">
        <h5 className="modal-title mt-0 d-flex align-items-center gap-2">
          <i className="fas fa-file-alt text-purple me-1"></i>
          Create amendment
          {contract?.company_name && (
            <span
              className="ms-2 badge"
              style={{
                background: "rgba(83,74,183,0.1)",
                color: "#534AB7",
                fontWeight: 400,
                fontSize: "11px",
              }}
            >
              {contract.company_name}
            </span>
          )}
        </h5>
        <button type="button" className="btn-modal-close" onClick={toggle}>
          <span className="btn-modal-span">&times;</span>
        </button>
      </div>

      {/* ── Body ── */}
      <div className="modal-body pt-3 pb-0" style={{ maxHeight: "72vh", overflowY: "auto" }}>
        {/* Info banner */}
        <div
          className="d-flex align-items-start gap-2 p-2 mb-3 rounded"
          style={{
            background: "rgba(255,193,7,0.08)",
            border: "0.5px solid rgba(255,193,7,0.35)",
            fontSize: "12px",
            color: "#856404",
          }}
        >
          <i className="fas fa-info-circle mt-1 flex-shrink-0"></i>
          <span>
            The original contract (Version 1) will remain unchanged. This amendment is recorded
            separately and must be approved by the client before the live contract updates.
          </span>
        </div>

        <Form onSubmit={handleSave}>
          {/* ── Section: Amendment Details ── */}
          <SectionHeader title="Amendment details" />
                

          <Row className="mb-3">
            <div className="col-12 mb-3">
              <FieldLabel required>Amendment description</FieldLabel>
              <textarea
                className={`form-control${errors.description ? " is-invalid" : ""}`}
                placeholder="Describe what is being changed and why…"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              {errors.description && <div className="invalid-feedback">{errors.description}</div>}
            </div>

            <div className="col-md-6 mb-3">
              <FieldLabel required>Change reason</FieldLabel>
              <Select
                options={CHANGE_REASON_OPTIONS}
                value={changeReason}
                onChange={setChangeReason}
                placeholder="Select reason"
                classNamePrefix="select"
                styles={colourStyles}
              />
              {errors.changeReason && (
                <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                  {errors.changeReason}
                </div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <FieldLabel required>Effective date</FieldLabel>
              <input
                type="date"
                className={`form-control${errors.effectiveDate ? " is-invalid" : ""}`}
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
              {errors.effectiveDate && (
                <div className="invalid-feedback">{errors.effectiveDate}</div>
              )}
            </div>
          </Row>

          {/* ── Section: Financial & Hours ── */}
          <SectionHeader title="Financial & hours changes" icon="fas fa-coins" />

          <Row className="mb-3">
            <div className="col-md-6 mb-3">
              <FieldLabel>Hours change</FieldLabel>
              <div className="d-flex gap-2">
                <DirectionToggle
                  value={hoursDirection}
                  onChange={setHoursDirection}
                  addLabel="+ Add"
                  remLabel="− Remove"
                />
                <input
                  type="number"
                  className="form-control"
                  placeholder="0"
                  min="0"
                  value={hoursChange}
                  onChange={(e) => setHoursChange(e.target.value)}
                  style={{ fontSize: "13px" }}
                />
              </div>
              {(hoursChange && currentHours > 0) && (
                <div className="mt-1" style={{ fontSize: "12px", color: "#556ee6" }}>
                  {currentHours} hrs → <strong>{revisedHours} hrs</strong>
                </div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <FieldLabel>Fee change</FieldLabel>
              <div className="d-flex gap-2">
                <DirectionToggle
                  value={feeDirection}
                  onChange={setFeeDirection}
                  addLabel="+ Add"
                  remLabel="− Remove"
                />
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  min="0"
                  value={feeChange}
                  onChange={(e) => setFeeChange(e.target.value)}
                  style={{ fontSize: "13px" }}
                />
              </div>
              {feeChange && (
                <div className="mt-1" style={{ fontSize: "12px", color: "#556ee6" }}>
                  {sym}{contractValue.toLocaleString()} → <strong>{sym}{revisedValue.toLocaleString()}</strong>
                </div>
              )}
            </div>
          </Row>

          {/* Summary row */}
          {(feeChange || (hoursChange && currentHours > 0)) && (
            <div
              className="d-flex gap-3 p-2 mb-3 rounded"
              style={{
                background: "rgba(85,110,230,0.06)",
                border: "0.5px solid rgba(85,110,230,0.2)",
                fontSize: "13px",
              }}
            >
              {feeChange && (
                <span>
                  <span style={{ color: "#74788d" }}>Revised value:</span>{" "}
                  <strong style={{ color: feeDirection === "add" ? "#34c38f" : "#f46a6a" }}>
                    {sym}{revisedValue.toLocaleString()}
                  </strong>
                </span>
              )}
              {hoursChange && currentHours > 0 && (
                <span>
                  <span style={{ color: "#74788d" }}>Revised hours:</span>{" "}
                  <strong style={{ color: hoursDirection === "add" ? "#34c38f" : "#f46a6a" }}>
                    {revisedHours} hrs
                  </strong>
                </span>
              )}
            </div>
          )}

          {/* ── Section: Milestone Dates ── */}
          <SectionHeader title="Revised milestone dates" icon="fas fa-calendar-alt" />

          <div className="mb-3">
            {milestones.map((m, idx) => (
              <div key={idx} className="d-flex gap-2 mb-2 align-items-end">
                <div style={{ flex: 1 }}>
                  {idx === 0 && <FieldLabel>Milestone name</FieldLabel>}
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Project delivery"
                    value={m.name}
                    onChange={(e) => updateMilestone(idx, "name", e.target.value)}
                    style={{ fontSize: "13px" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  {idx === 0 && <FieldLabel>Revised date</FieldLabel>}
                  <input
                    type="date"
                    className="form-control"
                    value={m.revised_date}
                    onChange={(e) => updateMilestone(idx, "revised_date", e.target.value)}
                    style={{ fontSize: "13px" }}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  style={{ marginBottom: "1px", height: "36px", width: "36px", padding: 0 }}
                  onClick={() => removeMilestone(idx)}
                  disabled={milestones.length === 1}
                >
                  <i className="fas fa-trash-alt" style={{ fontSize: "11px" }}></i>
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mt-1"
              style={{ fontSize: "12px" }}
              onClick={addMilestone}
            >
              <i className="fas fa-plus me-1"></i> Add milestone
            </button>
          </div>

          {/* ── Section: Scope Changes ── */}
          <SectionHeader title="Scope additions / removals" icon="fas fa-list-ul" />

          <div className="mb-3">
            {scopeItems.map((s, idx) => (
              <div key={idx} className="d-flex gap-2 mb-2 align-items-end">
                <div>
                  {idx === 0 && <FieldLabel>Type</FieldLabel>}
                  <div
                    className="btn-group"
                    style={{ whiteSpace: "nowrap" }}
                    role="group"
                    aria-label="Scope type"
                  >
                    <button
                      type="button"
                      className={`btn btn-sm ${s.type === "add" ? "btn-success" : "btn-outline-secondary"}`}
                      style={{ fontSize: "11px", padding: "5px 10px" }}
                      onClick={() => updateScope(idx, "type", "add")}
                    >
                      + Add
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${s.type === "remove" ? "btn-danger" : "btn-outline-secondary"}`}
                      style={{ fontSize: "11px", padding: "5px 10px" }}
                      onClick={() => updateScope(idx, "type", "remove")}
                    >
                      − Remove
                    </button>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  {idx === 0 && <FieldLabel>Scope description</FieldLabel>}
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Describe scope addition or removal…"
                    value={s.description}
                    onChange={(e) => updateScope(idx, "description", e.target.value)}
                    style={{ fontSize: "13px" }}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  style={{ marginBottom: "1px", height: "36px", width: "36px", padding: 0 }}
                  onClick={() => removeScope(idx)}
                  disabled={scopeItems.length === 1}
                >
                  <i className="fas fa-trash-alt" style={{ fontSize: "11px" }}></i>
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mt-1"
              style={{ fontSize: "12px" }}
              onClick={addScope}
            >
              <i className="fas fa-plus me-1"></i> Add scope item
            </button>
          </div>
        </Form>
      </div>

      {/* ── Footer ── */}
      <div className="modal-footer">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={toggle}>
          <i className="dripicons-cross me-1 icon-center"></i> Cancel
        </button>
        <button
          type="button"
          className="btn btn-save btn-label btn-sm"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <i className="bx bx-loader bx-spin label-icon"></i> Saving…
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane me-1 label-icon"></i> Save & send to client
            </>
          )}
        </button>
      </div>
    </Modal>
  );
};

/* ── Sub-components ── */

const SectionHeader = ({ title, icon }) => (
  <div
    className="d-flex align-items-center gap-2 mb-2 pb-2"
    style={{
      borderBottom: "1px solid #eff2f7",
      fontSize: "12px",
      fontWeight: 500,
      color: "#495057",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    }}
  >
    <i className={`${icon}`} style={{ color: "#556ee6", fontSize: "13px" }}></i>
    {title}
  </div>
);

const FieldLabel = ({ children, required }) => (
  <label className="mb-1" style={{ fontSize: "13px", color: "#495057", display: "block" }}>
    {children}
    {required && <span className="text-danger ms-1">*</span>}
  </label>
);

const DirectionToggle = ({ value, onChange, addLabel, remLabel }) => (
  <div className="btn-group flex-shrink-0" role="group">
    <button
      type="button"
      className={`btn btn-sm ${value === "add" ? "btn-success" : "btn-outline-secondary"}`}
      style={{ fontSize: "11px", padding: "5px 8px", whiteSpace: "nowrap" }}
      onClick={() => onChange("add")}
    >
      {addLabel}
    </button>
    <button
      type="button"
      className={`btn btn-sm ${value === "remove" ? "btn-danger" : "btn-outline-secondary"}`}
      style={{ fontSize: "11px", padding: "5px 8px", whiteSpace: "nowrap" }}
      onClick={() => onChange("remove")}
    >
      {remLabel}
    </button>
  </div>
);

export default ContractAmendmentModal;




















// import React, { useState, useEffect } from "react";
// import { Modal, Form } from "reactstrap";
// import Select from "react-select";
// import Swal from "sweetalert2";
// import moment from "moment";

/**
 * ContractAmendmentModal
 *
 * Props:
 *   isOpen           — boolean
 *   toggle           — fn to close
 *   contract         — { id, company_name, value, currency_symbol, hours }
 *   onAmendmentSaved — fn(payload) called on success
 */

// ── Constants ────────────────────────────────────────────────────────────────

// const CHANGE_REASON_OPTIONS = [
//   { label: "Client requested additional scope", value: "additional_scope" },
//   { label: "Revised timeline",                  value: "revised_timeline"  },
//   { label: "Pricing adjustment",                value: "pricing_adjustment" },
//   { label: "Resource change",                   value: "resource_change"   },
//   { label: "Reduced scope",                     value: "reduced_scope"     },
//   { label: "Other",                             value: "other"             },
// ];

// // Matches the rfp-select compact style
// const selectStyles = {
//   control: (b, { isFocused }) => ({
//     ...b,
//     minHeight: "36px",
//     fontSize: "13px",
//     borderColor: isFocused ? "#752a97" : "#d8bfe8",
//     boxShadow: isFocused ? "0 0 0 2px rgba(117,42,151,0.10)" : "none",
//     "&:hover": { borderColor: "#752a97" },
//   }),
//   menu:     (b) => ({ ...b, zIndex: 1300, fontSize: "13px" }),
//   menuList: (b) => ({ ...b, background: "#fff" }),
//   option: (b, { isFocused, isSelected }) => ({
//     ...b,
//     background: isSelected ? "#752a97" : isFocused ? "#f0e6f6" : "#fff",
//     color: isSelected ? "#fff" : "#330148",
//   }),
//   singleValue: (b) => ({ ...b, color: "#330148" }),
// };

// // ── Helpers ──────────────────────────────────────────────────────────────────

// const emptyMilestone = () => ({ name: "", revised_date: "" });
// const emptyScope     = () => ({ type: "add", description: "" });

// // ── Sub-components ────────────────────────────────────────────────────────────

// /** Purple left-border section title — matches rfp inner header style */
// const SectionTitle = ({ icon, children }) => (
//   <div
//     style={{
//       display: "flex",
//       alignItems: "center",
//       gap: 8,
//       padding: "7px 14px",
//       background: "#f0e6f6",
//       borderLeft: "4px solid #330148",
//       borderRadius: 6,
//       marginBottom: 14,
//       marginTop: 4,
//     }}
//   >
//     <i className={icon} style={{ color: "#752a97", fontSize: 13 }} />
//     <span style={{ fontWeight: 700, fontSize: 12, color: "#330148", textTransform: "uppercase", letterSpacing: "0.04em" }}>
//       {children}
//     </span>
//   </div>
// );

// /** Small field label */
// const Lbl = ({ children, required }) => (
//   <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
//     {children}{required && <span style={{ color: "#c43d5a", marginLeft: 2 }}>*</span>}
//   </label>
// );

// /** +Add / −Remove toggle pair — matches rfp-toggle style */
// const DirToggle = ({ value, onChange, disabled }) => (
//   <div
//     style={{
//       display: "flex",
//       border: "1px solid #752a97",
//       borderRadius: 50,
//       overflow: "hidden",
//       background: "#f9f4fc",
//       flexShrink: 0,
//     }}
//   >
//     {[{ val: "add", label: "+ Add" }, { val: "remove", label: "− Remove" }].map(({ val, label }) => (
//       <button
//         key={val}
//         type="button"
//         disabled={disabled}
//         onClick={() => onChange(val)}
//         style={{
//           padding: "5px 13px",
//           border: "none",
//           background: value === val ? "#752a97" : "transparent",
//           color: value === val ? "#fff" : "#752a97",
//           fontWeight: 600,
//           fontSize: 12,
//           cursor: disabled ? "not-allowed" : "pointer",
//           transition: "0.15s",
//           whiteSpace: "nowrap",
//         }}
//       >
//         {label}
//       </button>
//     ))}
//   </div>
// );

// /** Revised-value preview pill */
// const RevisionPill = ({ original, revised, symbol, unit }) => {
//   if (!revised && revised !== 0) return null;
//   const changed = revised !== original;
//   return (
//     <div
//       style={{
//         display: "inline-flex",
//         alignItems: "center",
//         gap: 6,
//         marginTop: 6,
//         background: "#f0e6f6",
//         border: "1px solid #d8bfe8",
//         borderRadius: 20,
//         padding: "2px 12px",
//         fontSize: 11,
//         color: "#330148",
//         fontWeight: 500,
//       }}
//     >
//       <span style={{ color: "#999" }}>{symbol}{original}{unit}</span>
//       <i className="fas fa-arrow-right" style={{ fontSize: 9, color: "#752a97" }} />
//       <span style={{ color: changed ? "#752a97" : "#330148", fontWeight: 700 }}>
//         {symbol}{revised}{unit}
//       </span>
//     </div>
//   );
// };

// // ── Main Component ────────────────────────────────────────────────────────────

// const ContractAmendmentModal = ({ isOpen, toggle, contract = {}, onAmendmentSaved }) => {
//   const [description,   setDescription]   = useState("");
//   const [changeReason,  setChangeReason]  = useState(null);
//   const [effectiveDate, setEffectiveDate] = useState("");
//   const [hoursDir,      setHoursDir]      = useState("add");
//   const [hoursChange,   setHoursChange]   = useState("");
//   const [feeDir,        setFeeDir]        = useState("add");
//   const [feeChange,     setFeeChange]     = useState("");
//   const [milestones,    setMilestones]    = useState([emptyMilestone()]);
//   const [scopeItems,    setScopeItems]    = useState([emptyScope()]);
//   const [isSaving,      setIsSaving]      = useState(false);
//   const [errors,        setErrors]        = useState({});

//   // Derived numbers
//   const contractValue  = parseFloat(contract?.value) || 0;
//   const currentHours   = parseFloat(contract?.hours) || 0;
//   const feeNum         = parseFloat(feeChange)   || 0;
//   const hoursNum       = parseFloat(hoursChange) || 0;
//   const revisedValue   = feeDir   === "add" ? contractValue + feeNum   : contractValue - feeNum;
//   const revisedHours   = hoursDir === "add" ? currentHours + hoursNum  : currentHours - hoursNum;
//   const sym            = contract?.currency_symbol || "£";

//   useEffect(() => { if (!isOpen) reset(); }, [isOpen]);

//   const reset = () => {
//     setDescription(""); setChangeReason(null); setEffectiveDate("");
//     setHoursDir("add"); setHoursChange(""); setFeeDir("add"); setFeeChange("");
//     setMilestones([emptyMilestone()]); setScopeItems([emptyScope()]); setErrors({});
//   };

//   const validate = () => {
//     const e = {};
//     if (!description.trim()) e.description  = "Required";
//     if (!changeReason)        e.changeReason = "Required";
//     if (!effectiveDate)       e.effectiveDate = "Required";
//     setErrors(e);
//     return !Object.keys(e).length;
//   };

//   const handleSave = async (e) => {
//     e.preventDefault();
//     if (!validate()) return;
//     const payload = {
//       contract_id:    contract?.id,
//       description,
//       change_reason:  changeReason?.value,
//       effective_date: effectiveDate,
//       hours_direction: hoursDir, hours_change: hoursNum, revised_hours: revisedHours,
//       fee_direction:  feeDir,   fee_change:   feeNum,   revised_value: revisedValue,
//       milestones:     milestones.filter(m => m.name || m.revised_date),
//       scope_items:    scopeItems.filter(s => s.description),
//       created_at:     moment().format("YYYY-MM-DD HH:mm:ss"),
//     };
//     setIsSaving(true);
//     try {
//       // await axiosNodeInstance.post("/contract/amendment", payload);
//       await new Promise(r => setTimeout(r, 700));
//       Swal.fire({ icon: "success", text: "Amendment created and sent to client for approval.", timer: 2500, showConfirmButton: false });
//       onAmendmentSaved?.(payload);
//       toggle();
//     } catch {
//       Swal.fire({ icon: "error", text: "Something went wrong. Please try again." });
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   // Milestone helpers
//   const updMilestone = (i, f, v) => { const a = [...milestones]; a[i][f] = v; setMilestones(a); };
//   const addMilestone = () => setMilestones([...milestones, emptyMilestone()]);
//   const delMilestone = (i) => setMilestones(milestones.filter((_, idx) => idx !== i));

//   // Scope helpers
//   const updScope = (i, f, v) => { const a = [...scopeItems]; a[i][f] = v; setScopeItems(a); };
//   const addScope = () => setScopeItems([...scopeItems, emptyScope()]);
//   const delScope = (i) => setScopeItems(scopeItems.filter((_, idx) => idx !== i));

//   return (
//     <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>

//       {/* ── Header ── */}
//       <div className="modal-header" style={{ borderBottom: "1px solid #ede0f5", background: "#faf6fc" }}>
//         <h5 className="modal-title mt-0" style={{ color: "#330148", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
//           <i className="fas fa-file-signature" style={{ color: "#752a97" }} />
//           Create Amendment
//           {contract?.company_name && (
//             <span className="rfp-count-pill" style={{ fontSize: 11 }}>{contract.company_name}</span>
//           )}
//         </h5>
//         <button type="button" className="btn-modal-close" onClick={toggle}>
//           <span className="btn-modal-span">&times;</span>
//         </button>
//       </div>

//       {/* ── Body ── */}
//       <div className="modal-body pt-3 pb-2" style={{ maxHeight: "74vh", overflowY: "auto" }}>

//         {/* Info notice */}
//         <div
//           style={{
//             display: "flex", gap: 10, alignItems: "flex-start",
//             background: "#f0e6f6", border: "1px solid #d8bfe8",
//             borderRadius: 8, padding: "10px 14px", marginBottom: 18, fontSize: 12, color: "#330148",
//           }}
//         >
//           <i className="fas fa-info-circle" style={{ color: "#752a97", marginTop: 1, flexShrink: 0 }} />
//           <span>
//             The original contract (Version 1) remains locked and unchanged.
//             This amendment will be sent to the client for signature before the live contract updates.
//           </span>
//         </div>

//         <Form onSubmit={handleSave}>

//           {/* ── Section 1: Details ── */}
//           <SectionTitle icon="fas fa-pencil-alt">Amendment Details</SectionTitle>

//           <div className="mb-3">
//             <Lbl required>Description</Lbl>
//             <textarea
//               className={`form-control${errors.description ? " is-invalid" : ""}`}
//               style={{ fontSize: 13, minHeight: 72, borderColor: errors.description ? "#c43d5a" : "#d8bfe8", resize: "vertical" }}
//               placeholder="Describe what is being changed and why…"
//               value={description}
//               onChange={e => { setDescription(e.target.value); setErrors(p => ({ ...p, description: "" })); }}
//             />
//             {errors.description && <div className="invalid-feedback">{errors.description}</div>}
//           </div>

//           <div className="row g-3 mb-1">
//             <div className="col-md-6">
//               <Lbl required>Change Reason</Lbl>
//               <Select
//                 options={CHANGE_REASON_OPTIONS}
//                 value={changeReason}
//                 onChange={v => { setChangeReason(v); setErrors(p => ({ ...p, changeReason: "" })); }}
//                 placeholder="Select reason…"
//                 styles={selectStyles}
//               />
//               {errors.changeReason && <div style={{ color: "#c43d5a", fontSize: 11, marginTop: 3 }}>{errors.changeReason}</div>}
//             </div>
//             <div className="col-md-6">
//               <Lbl required>Effective Date</Lbl>
//               <input
//                 type="date"
//                 className={`form-control${errors.effectiveDate ? " is-invalid" : ""}`}
//                 style={{ fontSize: 13, borderColor: errors.effectiveDate ? "#c43d5a" : "#d8bfe8" }}
//                 value={effectiveDate}
//                 onChange={e => { setEffectiveDate(e.target.value); setErrors(p => ({ ...p, effectiveDate: "" })); }}
//               />
//               {errors.effectiveDate && <div className="invalid-feedback">{errors.effectiveDate}</div>}
//             </div>
//           </div>

//           {/* ── Section 2: Financial & Hours ── */}
//           <SectionTitle icon="fas fa-coins">Financial &amp; Hours Changes</SectionTitle>

//           <div className="row g-3 mb-1">
//             {/* Hours */}
//             <div className="col-md-6">
//               <Lbl>Hours Change</Lbl>
//               <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
//                 <DirToggle value={hoursDir} onChange={setHoursDir} />
//                 <input
//                   type="number" min="0" placeholder="0"
//                   className="form-control"
//                   style={{ fontSize: 13, borderColor: "#d8bfe8", flex: 1 }}
//                   value={hoursChange}
//                   onChange={e => setHoursChange(e.target.value)}
//                 />
//               </div>
//               {hoursChange && currentHours > 0 && (
//                 <RevisionPill original={currentHours} revised={revisedHours} symbol="" unit=" hrs" />
//               )}
//             </div>
//             {/* Fee */}
//             <div className="col-md-6">
//               <Lbl>Fee Change</Lbl>
//               <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
//                 <DirToggle value={feeDir} onChange={setFeeDir} />
//                 <input
//                   type="number" min="0" placeholder="0.00"
//                   className="form-control"
//                   style={{ fontSize: 13, borderColor: "#d8bfe8", flex: 1 }}
//                   value={feeChange}
//                   onChange={e => setFeeChange(e.target.value)}
//                 />
//               </div>
//               {feeChange && (
//                 <RevisionPill original={contractValue.toLocaleString()} revised={revisedValue.toLocaleString()} symbol={sym} unit="" />
//               )}
//             </div>
//           </div>

//           {/* Grand total preview */}
//           {(feeChange || (hoursChange && currentHours > 0)) && (
//             <div className="rfp-total-bar" style={{ marginTop: 12, marginBottom: 4 }}>
//               <span className="rfp-total-label" style={{ fontSize: 13 }}>Revised Contract Summary</span>
//               <div style={{ display: "flex", gap: 20 }}>
//                 {feeChange && (
//                   <span className="rfp-total-value" style={{ fontSize: 15 }}>
//                     {sym}{revisedValue.toLocaleString()}
//                   </span>
//                 )}
//                 {hoursChange && currentHours > 0 && (
//                   <span className="rfp-timing-pill">{revisedHours} hrs</span>
//                 )}
//               </div>
//             </div>
//           )}

//           {/* ── Section 3: Milestones ── */}
//           <SectionTitle icon="fas fa-calendar-alt">Revised Milestone Dates</SectionTitle>

//           <div className="mb-2">
//             {milestones.map((m, idx) => (
//               <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 32px", gap: 8, marginBottom: 8, alignItems: "end" }}>
//                 <div>
//                   {idx === 0 && <Lbl>Milestone Name</Lbl>}
//                   <input
//                     type="text" placeholder="e.g. Project delivery"
//                     className="form-control"
//                     style={{ fontSize: 13, borderColor: "#d8bfe8" }}
//                     value={m.name}
//                     onChange={e => updMilestone(idx, "name", e.target.value)}
//                   />
//                 </div>
//                 <div>
//                   {idx === 0 && <Lbl>Revised Date</Lbl>}
//                   <input
//                     type="date"
//                     className="form-control"
//                     style={{ fontSize: 13, borderColor: "#d8bfe8" }}
//                     value={m.revised_date}
//                     onChange={e => updMilestone(idx, "revised_date", e.target.value)}
//                   />
//                 </div>
//                 <button
//                   type="button"
//                   className="rfp-remove-btn"
//                   style={{ borderRadius: 6, height: 36, marginTop: idx === 0 ? 20 : 0 }}
//                   onClick={() => delMilestone(idx)}
//                   disabled={milestones.length === 1}
//                 >
//                   <i className="fas fa-trash-alt" style={{ fontSize: 11 }} />
//                 </button>
//               </div>
//             ))}
//             <button type="button" className="rfp-add-btn btn btn-sm" onClick={addMilestone}
//               style={{ fontSize: 12 }}>
//               <i className="fas fa-plus me-1" /> Add Milestone
//             </button>
//           </div>

//           {/* ── Section 4: Scope ── */}
//           <SectionTitle icon="fas fa-list-ul">Scope Additions / Removals</SectionTitle>

//           <div className="mb-2">
//             {scopeItems.map((s, idx) => (
//               <div key={idx} style={{ display: "grid", gridTemplateColumns: "auto 1fr 32px", gap: 8, marginBottom: 8, alignItems: "end" }}>
//                 <div>
//                   {idx === 0 && <Lbl>Type</Lbl>}
//                   <DirToggle value={s.type} onChange={v => updScope(idx, "type", v)} />
//                 </div>
//                 <div>
//                   {idx === 0 && <Lbl>Description</Lbl>}
//                   <input
//                     type="text"
//                     placeholder="Describe the scope change…"
//                     className="form-control"
//                     style={{ fontSize: 13, borderColor: "#d8bfe8" }}
//                     value={s.description}
//                     onChange={e => updScope(idx, "description", e.target.value)}
//                   />
//                 </div>
//                 <button
//                   type="button"
//                   className="rfp-remove-btn"
//                   style={{ borderRadius: 6, height: 36, marginTop: idx === 0 ? 20 : 0 }}
//                   onClick={() => delScope(idx)}
//                   disabled={scopeItems.length === 1}
//                 >
//                   <i className="fas fa-trash-alt" style={{ fontSize: 11 }} />
//                 </button>
//               </div>
//             ))}
//             <button type="button" className="rfp-add-btn btn btn-sm" onClick={addScope}
//               style={{ fontSize: 12 }}>
//               <i className="fas fa-plus me-1" /> Add Scope Item
//             </button>
//           </div>

//         </Form>
//       </div>

//       {/* ── Footer ── */}
//       <div
//         className="modal-footer"
//         style={{ borderTop: "1px solid #ede0f5", background: "#faf6fc", justifyContent: "space-between" }}
//       >
//         <button
//           type="button"
//           onClick={toggle}
//           style={{
//             background: "transparent", border: "1px solid #d8bfe8",
//             color: "#752a97", borderRadius: 6, padding: "6px 16px",
//             fontSize: 13, fontWeight: 600, cursor: "pointer",
//           }}
//         >
//           <i className="dripicons-cross me-1" /> Cancel
//         </button>
//         <button
//           type="button"
//           onClick={handleSave}
//           disabled={isSaving}
//           className="rfp-next-btn btn text-white btn-label"
//           style={{ fontSize: 13, fontWeight: 600 }}
//         >
//           {isSaving
//             ? <><i className="bx bx-loader bx-spin label-icon" /> Saving…</>
//             : <><i className="fas fa-paper-plane me-1 label-icon" /> Save &amp; Send to Client</>
//           }
//         </button>
//       </div>

//     </Modal>
//   );
// };

// export default ContractAmendmentModal;